import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Send, Loader2, Mail, Users } from "lucide-react";

interface BroadcastLog {
  id: string;
  subject: string;
  recipient_count: number;
  success_count: number;
  failed_count: number;
  created_at: string;
  audience_filter: any;
}

const BroadcastEmails = () => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [plan, setPlan] = useState<string>("all");
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [logs, setLogs] = useState<BroadcastLog[]>([]);
  const [unsubCount, setUnsubCount] = useState(0);
  const [profilesCount, setProfilesCount] = useState(0);

  const syncToResend = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-resend-contacts");
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Failed");
      const d = data.data;
      toast.success(
        `Synced ${d.created} new · ${d.skipped_existing} already there · ${d.failed} failed`,
      );
    } catch (e: any) {
      toast.error(e?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const loadData = async () => {
    const { data: l } = await supabase
      .from("broadcast_logs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);
    setLogs((l as any) || []);

    const { count: uc } = await supabase
      .from("email_unsubscribes")
      .select("*", { count: "exact", head: true });
    setUnsubCount(uc || 0);

    const { count: pc } = await supabase
      .from("profiles")
      .select("*", { count: "exact", head: true });
    setProfilesCount(pc || 0);
  };

  useEffect(() => {
    loadData();
  }, []);

  const send = async (isTest: boolean) => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    if (isTest && !testEmail.trim()) {
      toast.error("Enter a test email");
      return;
    }
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "send-broadcast-email",
        {
          body: {
            subject,
            body_html: body,
            plan: plan === "all" ? null : plan,
            test_email: isTest ? testEmail.trim() : null,
          },
        },
      );
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Failed");
      toast.success(
        isTest
          ? `Test sent to ${testEmail}`
          : `Sent to ${data.data.success} / ${data.data.recipients} recipients`,
      );
      if (!isTest) {
        setSubject("");
        setBody("");
        loadData();
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              Total Users
            </p>
            <h3 className="text-2xl font-bold mt-1">{profilesCount}</h3>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              Unsubscribed
            </p>
            <h3 className="text-2xl font-bold mt-1">{unsubCount}</h3>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              Eligible recipients
            </p>
            <h3 className="text-2xl font-bold mt-1">
              {Math.max(0, profilesCount - unsubCount)}
            </h3>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Sync contacts to Resend
          </CardTitle>
          <Button onClick={syncToResend} disabled={syncing} variant="outline">
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Users className="h-4 w-4 mr-2" />
            )}
            Sync now
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Pushes all eligible user emails into your Resend Audience so you can
            also send broadcasts from the Resend dashboard. Existing contacts are
            skipped automatically. Unsubscribed users are excluded.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Compose Broadcast
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Audience</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  <SelectItem value="free">Free plan</SelectItem>
                  <SelectItem value="starter">Starter plan</SelectItem>
                  <SelectItem value="pro">Pro plan</SelectItem>
                  <SelectItem value="scale">Scale plan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Test send to</Label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Subject</Label>
            <Input
              placeholder="Big news from Bravax Forms..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Body (HTML allowed)</Label>
            <Textarea
              rows={12}
              placeholder="<h2>Hello!</h2><p>Your message...</p>"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              An unsubscribe link is automatically appended to every email
              (CAN-SPAM/GDPR compliant).
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => send(true)}
              disabled={sending}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Send test
            </Button>
            <Button onClick={() => send(false)} disabled={sending}>
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send to {plan === "all" ? "all" : plan} users
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Broadcast history</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">
              No broadcasts sent yet.
            </p>
          ) : (
            <div className="divide-y divide-border/40">
              {logs.map((l) => (
                <div
                  key={l.id}
                  className="p-4 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{l.subject}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(l.created_at).toLocaleString()} ·{" "}
                      {l.audience_filter?.plan || "all"} plan
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="secondary">
                      {l.success_count}/{l.recipient_count} sent
                    </Badge>
                    {l.failed_count > 0 && (
                      <Badge variant="destructive">
                        {l.failed_count} failed
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BroadcastEmails;
